import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Play, Pause, RotateCcw, Volume2, VolumeX, Activity, ChevronRight, Square, Vibrate, Save, Share2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'apnea_execution_state_v1';

const formatMMSS = (seconds) => {
  if (seconds < 0) seconds = 0;
  const mm = Math.floor(seconds / 60);
  const ss = Math.floor(seconds % 60);
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

// Speech synthesis helper
const speak = (text, enabled, opts = {}) => {
  if (!enabled || typeof window === 'undefined' || !window.speechSynthesis || !text) return;
  try {
    window.speechSynthesis.cancel();
    const utt = new window.SpeechSynthesisUtterance(text);
    utt.lang = opts.lang || 'fr-FR';
    utt.rate = opts.rate || 1.0;
    utt.pitch = opts.pitch || 1.0;
    utt.volume = opts.volume != null ? opts.volume : 1.0;
    if (opts.voiceName) {
      const voices = window.speechSynthesis.getVoices();
      const match = voices.find((v) => v.name === opts.voiceName);
      if (match) utt.voice = match;
    }
    window.speechSynthesis.speak(utt);
  } catch (e) {
    console.warn('Speech synthesis failed:', e);
  }
};

const vibrate = (pattern, enabled) => {
  if (!enabled || typeof navigator === 'undefined' || !navigator.vibrate) return;
  try {
    navigator.vibrate(pattern);
  } catch (e) {
    console.warn('Vibrate failed:', e);
  }
};

// Circular progress component (SVG)
const CircularProgress = ({ progress, size = 320, stroke = 16, color = '#10b981', bgColor = '#1e3a5f', innerColor = '#22d3ee', innerProgress = 0 }) => {
  const radius = (size - stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - progress * circumference;
  const innerRadius = radius - stroke - 8;
  const innerCircumference = innerRadius * 2 * Math.PI;
  const innerOffset = innerCircumference - innerProgress * innerCircumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {/* Background outer ring (purple = total table progress) */}
      <circle cx={size / 2} cy={size / 2} r={radius} stroke="#1e1b4b" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#a855f7"
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.3s ease' }}
      />
      {/* Inner ring (current step) */}
      <circle cx={size / 2} cy={size / 2} r={innerRadius} stroke={bgColor} strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={innerRadius}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={innerCircumference}
        strokeDashoffset={innerOffset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.3s ease' }}
      />
    </svg>
  );
};

const TableExecutionView = ({ table, pbMode = false, onExit, freedivers = [], voiceSettings = {} }) => {
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [stepElapsed, setStepElapsed] = useState(0); // seconds elapsed in current step
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [vibrateEnabled, setVibrateEnabled] = useState(true);
  const [contractions, setContractions] = useState([]); // {step_idx, time_in_step, total_elapsed}
  const [completed, setCompleted] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);
  const [saveDialog, setSaveDialog] = useState(false);
  const [selectedFreediverId, setSelectedFreediverId] = useState('');
  const [saveToCareer, setSaveToCareer] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [pbAchieved, setPbAchieved] = useState(0); // tracks longest apnea in PB mode
  const [partialEnd, setPartialEnd] = useState(false); // user manually ended table early
  const [confirmEndTable, setConfirmEndTable] = useState(false);
  const [resumeData, setResumeData] = useState(null); // saved state pending user's resume decision
  const startedAtRef = useRef(null);

  const wakeLockRef = useRef(null);
  const intervalRef = useRef(null);
  const lastTickRef = useRef(null);
  const announcedRef = useRef(new Set()); // track which countdown values we've spoken for current step

  const steps = table?.steps || [];
  const currentStep = steps[currentStepIdx] || null;
  const stepDuration = currentStep?.duration_seconds || 0;
  const stepRemaining = Math.max(0, stepDuration - stepElapsed);
  const isApnea = currentStep?.phase === 'apnea';

  // Total table duration & overall progress
  const totalDuration = useMemo(() => steps.reduce((acc, s) => acc + (s.duration_seconds || 0), 0), [steps]);
  const overallProgress = totalDuration > 0 ? Math.min(1, totalElapsed / totalDuration) : 0;
  const stepProgress = stepDuration > 0 ? Math.min(1, stepElapsed / stepDuration) : 0;

  // Rounds logic — 1 round = 1 pair of (ventilation + apnea) or (apnea + ventilation)
  const totalRounds = Math.max(1, Math.ceil(steps.length / 2));
  const currentRound = Math.min(totalRounds, Math.floor(currentStepIdx / 2) + 1);
  const phaseInRound = (currentStepIdx % 2) + 1; // 1 or 2
  const phasesInCurrentRound = (currentStepIdx + 1 < steps.length && (currentStepIdx % 2 === 0)) || (currentStepIdx % 2 === 1) ? 2 : 1;
  // Next round preview (phase 1 and 2 of round currentRound+1)
  const nextRoundFirstStepIdx = (currentRound) * 2; // 0-based index for next round's first step
  const nextRoundStepA = steps[nextRoundFirstStepIdx] || null;
  const nextRoundStepB = steps[nextRoundFirstStepIdx + 1] || null;

  // Wake Lock — keep screen on during execution
  const acquireWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch (e) {
      console.warn('WakeLock failed:', e);
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (e) {
      console.warn('WakeLock release failed:', e);
    }
  }, []);

  // Re-acquire wake lock on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && running && !wakeLockRef.current) {
        acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      releaseWakeLock();
    };
  }, [running, acquireWakeLock, releaseWakeLock]);

  // Auto-resume from localStorage — surface an in-app dialog (no native confirm)
  useEffect(() => {
    if (!table || !table.id) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.tableId === table.id && saved.currentStepIdx < (steps.length || 0)) {
          setResumeData(saved);
        }
      }
    } catch (e) {
      console.warn('Auto-resume check failed:', e);
    }
  }, []);

  const handleResumeAccept = () => {
    if (resumeData) {
      setCurrentStepIdx(resumeData.currentStepIdx || 0);
      setStepElapsed(resumeData.stepElapsed || 0);
      setTotalElapsed(resumeData.totalElapsed || 0);
      setContractions(resumeData.contractions || []);
    }
    setResumeData(null);
  };

  const handleResumeDecline = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    setResumeData(null);
  };

  // Persist state on changes
  useEffect(() => {
    if (!table?.id) return;
    if (running || stepElapsed > 0 || totalElapsed > 0) {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            tableId: table.id,
            currentStepIdx,
            stepElapsed,
            totalElapsed,
            contractions,
            ts: Date.now(),
          })
        );
      } catch (e) {
        console.warn('Persist failed:', e);
      }
    }
  }, [running, currentStepIdx, stepElapsed, totalElapsed, contractions, table?.id]);

  // Voice announcements for the current step (anticipated by 1s to compensate TTS latency)
  useEffect(() => {
    if (!running || !currentStep) return;

    const remaining = Math.ceil(stepRemaining);
    const ann = announcedRef.current;

    // Major countdowns — fired 1s before the actual mark
    if ([61, 31, 16, 11].includes(remaining) && !ann.has(`${currentStepIdx}-${remaining}`)) {
      speakIfPhaseAllowed(formatTpl(VS.text_long_countdown || '{n} secondes', remaining - 1), audioEnabled);
      ann.add(`${currentStepIdx}-${remaining}`);
    }
    // Final countdown 5,4,3,2,1 — fired 1s early (when remaining=6, speak "5")
    if (remaining >= 2 && remaining <= 6 && !ann.has(`${currentStepIdx}-${remaining}`)) {
      speakIfPhaseAllowed(formatTpl(VS.text_final_countdown || '{n}', remaining - 1), audioEnabled);
      ann.add(`${currentStepIdx}-${remaining}`);
    }
  }, [stepRemaining, currentStepIdx, running, audioEnabled, isApnea, currentStep]);

  // Tick — main timer loop
  useEffect(() => {
    if (!running) {
      clearInterval(intervalRef.current);
      return;
    }
    lastTickRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const delta = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      setStepElapsed((prev) => prev + delta);
      setTotalElapsed((prev) => prev + delta);
    }, 250);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  // Step transition
  useEffect(() => {
    if (!currentStep || pbMode) return;
    if (stepElapsed >= stepDuration && stepDuration > 0) {
      // Track PB if this was an apnea step
      if (currentStep.phase === 'apnea') {
        setPbAchieved((prev) => Math.max(prev, stepDuration));
      }
      const nextIdx = currentStepIdx + 1;
      if (nextIdx >= steps.length) {
        // Completed!
        setRunning(false);
        setCompleted(true);
        speak(VS.text_table_complete || 'Table terminée. Bravo !', audioEnabled && VS.enabled !== false, voiceOpts);
        vibrate([200, 100, 200, 100, 400], vibrateEnabled);
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (e) {}
      } else {
        const nextStep = steps[nextIdx];
        // Phase transition — speak + vibrate
        if (nextStep.phase === 'apnea') {
          speak(VS.text_start_apnea || "Commencer l'apnée", audioEnabled && VS.enabled !== false, voiceOpts);
          vibrate([200, 50, 200], vibrateEnabled);
        } else {
          speak(VS.text_start_ventilation || 'Respirez', audioEnabled && VS.enabled !== false, voiceOpts);
          vibrate([400], vibrateEnabled);
        }
        announcedRef.current = new Set();
        setCurrentStepIdx(nextIdx);
        setStepElapsed(0);
      }
    }
  }, [stepElapsed, stepDuration, currentStepIdx, steps.length, pbMode]);

  const handleStart = async () => {
    if (steps.length === 0) return;
    setRunning(true);
    setCompleted(false);
    if (!startedAtRef.current) {
      startedAtRef.current = new Date().toISOString();
    }
    await acquireWakeLock();
    const firstStep = currentStep || steps[0];
    if (firstStep) {
      if (firstStep.phase === 'apnea') {
        speak(VS.text_start_apnea || "Commencer l'apnée", audioEnabled && VS.enabled !== false, voiceOpts);
        vibrate([200, 50, 200], vibrateEnabled);
      } else {
        speak('Démarrage. ' + (VS.text_start_ventilation || 'Respirez') + '.', audioEnabled && VS.enabled !== false, voiceOpts);
        vibrate([200], vibrateEnabled);
      }
    }
  };

  const handlePauseResume = () => {
    if (running) {
      setRunning(false);
      releaseWakeLock();
    } else {
      handleStart();
    }
  };

  const handleReset = () => {
    setRunning(false);
    setCurrentStepIdx(0);
    setStepElapsed(0);
    setTotalElapsed(0);
    setContractions([]);
    setCompleted(false);
    setPartialEnd(false);
    setPbAchieved(0);
    startedAtRef.current = null;
    announcedRef.current = new Set();
    releaseWakeLock();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  };

  const handleAddContraction = () => {
    if (!isApnea || !running) return;
    const entry = {
      step_idx: currentStepIdx,
      time_in_step: stepElapsed,
      total_elapsed: totalElapsed,
    };
    setContractions((prev) => [...prev, entry]);
    vibrate([50], vibrateEnabled);
  };

  const handleEndApneaPB = () => {
    // PB mode — end the current "infinite" apnea
    if (pbMode && isApnea) {
      setRunning(false);
      setCompleted(true);
      setPbAchieved(stepElapsed);
      releaseWakeLock();
      speak(`Apnée terminée. Durée : ${formatMMSS(stepElapsed)}`, audioEnabled && VS.enabled !== false, voiceOpts);
      vibrate([200, 100, 200, 100, 400], vibrateEnabled);
    }
  };

  // Save execution session to backend (history + optional career link)
  const handleSaveSession = async () => {
    try {
      const payload = {
        table_id: table.id,
        table_name: table.name,
        table_type: table.type,
        is_pb_mode: pbMode,
        started_at: startedAtRef.current || new Date().toISOString(),
        completed_at: new Date().toISOString(),
        duration_seconds: totalElapsed,
        completed: completed && !partialEnd,
        max_apnea_seconds: pbAchieved,
        contractions: contractions,
        freediver_id: selectedFreediverId || null,
        notes: sessionNotes || '',
        save_to_career: saveToCareer && pbMode && !!selectedFreediverId,
      };
      await axios.post(`${API_BASE_URL}/api/apnea-table-sessions`, payload);
      setSavedSuccess(true);
      setSaveDialog(false);
      setTimeout(() => setSavedSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save session:', err);
      alert(`Erreur de sauvegarde : ${err.response?.data?.detail || err.message}`);
    }
  };

  // Generate a shareable PNG recap of the session (Web Share API, fallback download)
  const handleShareRecap = async () => {
    try {
      const W = 1080, H = 1080;
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');

      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, '#0b1f3a');
      g.addColorStop(0.55, '#0e3550');
      g.addColorStop(1, '#0a3f43');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = 'rgba(34,211,238,0.9)';
      ctx.fillRect(0, 0, W, 10);

      ctx.textAlign = 'center';
      ctx.fillStyle = '#22d3ee';
      ctx.font = '700 44px Arial, sans-serif';
      ctx.fillText('APNEA COACH MANAGER', W / 2, 120);
      ctx.fillStyle = '#93c5fd';
      ctx.font = '400 30px Arial, sans-serif';
      ctx.fillText("Récap de séance — Table d'apnée", W / 2, 172);

      ctx.fillStyle = '#e0f2fe';
      ctx.font = '700 60px Arial, sans-serif';
      ctx.fillText((table?.name || 'Table').slice(0, 34), W / 2, 300);
      ctx.fillStyle = '#7dd3fc';
      ctx.font = '400 30px Arial, sans-serif';
      ctx.fillText(table?.type || '', W / 2, 350);

      const roundsDone = partialEnd ? Math.floor(currentStepIdx / 2) : totalRounds;
      const best = pbAchieved || (pbMode ? stepElapsed : 0);
      if (pbMode) {
        ctx.fillStyle = '#a5f3fc';
        ctx.font = '400 34px Arial, sans-serif';
        ctx.fillText('Meilleure apnée', W / 2, 500);
        ctx.fillStyle = '#ffffff';
        ctx.font = '800 168px Arial, sans-serif';
        ctx.fillText(formatMMSS(best), W / 2, 660);
      } else {
        ctx.fillStyle = '#a5f3fc';
        ctx.font = '400 34px Arial, sans-serif';
        ctx.fillText('Rounds complétés', W / 2, 500);
        ctx.fillStyle = '#ffffff';
        ctx.font = '800 160px Arial, sans-serif';
        ctx.fillText(`${roundsDone} / ${totalRounds}`, W / 2, 655);
      }

      if (!pbMode && totalRounds > 1 && totalRounds <= 40) {
        const gap = Math.min(46, Math.floor((W - 200) / totalRounds));
        const r = Math.max(8, Math.min(18, Math.floor(gap / 3)));
        const totalW = (totalRounds - 1) * gap;
        const x0 = W / 2 - totalW / 2;
        const y = 730;
        for (let i = 0; i < totalRounds; i++) {
          ctx.beginPath();
          ctx.arc(x0 + i * gap, y, r, 0, Math.PI * 2);
          if (i < roundsDone) { ctx.fillStyle = '#22d3ee'; ctx.fill(); }
          else { ctx.strokeStyle = 'rgba(148,197,253,0.55)'; ctx.lineWidth = 3; ctx.stroke(); }
        }
      }

      ctx.fillStyle = '#cbd5e1';
      ctx.font = '400 34px Arial, sans-serif';
      ctx.fillText(`Durée totale : ${formatMMSS(totalElapsed)}`, W / 2, 850);
      if (!pbMode) ctx.fillText(`Contractions : ${contractions.length}`, W / 2, 900);

      ctx.fillStyle = '#64748b';
      ctx.font = '400 28px Arial, sans-serif';
      ctx.fillText(
        new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }),
        W / 2, 1010
      );

      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `apnea-recap-${stamp}.png`;
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('génération image échouée');
      const file = new File([blob], fileName, { type: 'image/png' });
      const shareText = pbMode
        ? `Nouvelle apnée : ${formatMMSS(best)} sur "${table?.name}" 🌊`
        : `${roundsDone}/${totalRounds} rounds complétés sur "${table?.name}" 🌊`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Récap de séance', text: shareText });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fileName;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      if (e && e.name === 'AbortError') return; // user cancelled the native share sheet
      console.warn('Share recap failed:', e);
      alert(`Impossible de partager le récap : ${e.message || e}`);
    }
  };

  // End table early — keep already-executed steps as a partial session
  const handleEndTable = () => {
    setRunning(false);
    setCompleted(true);
    setPartialEnd(true);
    // Pre-fill notes with auto-prefix so user can see/edit before saving
    setSessionNotes(`[Table partielle — round ${currentRound}/${totalRounds}, phase ${phaseInRound}/${phasesInCurrentRound}] `);
    // If currently in apnea, capture the current apnea time as a candidate PB
    if (isApnea && stepElapsed > 0) {
      setPbAchieved((prev) => Math.max(prev, stepElapsed));
    }
    releaseWakeLock();
    speak('Table terminée', audioEnabled && VS.enabled !== false, voiceOpts);
    vibrate([300, 100, 300], vibrateEnabled);
    setConfirmEndTable(false);
  };

  const handleExitConfirm = () => {
    if (running || stepElapsed > 0 || totalElapsed > 0) {
      setConfirmExit(true);
    } else {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      releaseWakeLock();
      onExit();
    }
  };

  // Voice helpers respecting global voiceSettings
  const VS = voiceSettings || {};
  const voiceOpts = {
    lang: VS.voice_lang || 'fr-FR',
    voiceName: VS.voice_name || '',
    rate: VS.rate ?? 1.0,
    pitch: VS.pitch ?? 1.0,
    volume: VS.volume ?? 1.0,
  };
  const speakIfPhaseAllowed = (text, masterEnabled) => {
    if (!masterEnabled) return;
    if (VS.enabled === false) return;
    if (isApnea && VS.speak_during_apnea === false) return;
    if (!isApnea && VS.speak_during_ventilation === false) return;
    speak(text, true, voiceOpts);
  };
  const formatTpl = (tpl, n) => (tpl || '').replace('{n}', String(n));

  const phaseColor = isApnea ? '#ef4444' : '#10b981';
  const phaseBg = isApnea ? 'from-red-950 via-red-900 to-blue-950' : 'from-green-950 via-green-900 to-blue-950';

  // Stats during apnea
  const contractionsThisStep = contractions.filter((c) => c.step_idx === currentStepIdx);
  const firstContraction = contractionsThisStep[0]?.time_in_step;

  // Responsive circle size
  const [viewportSize, setViewportSize] = useState({ w: typeof window !== 'undefined' ? window.innerWidth : 1024, h: typeof window !== 'undefined' ? window.innerHeight : 768 });
  useEffect(() => {
    const onResize = () => setViewportSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  // Conservative size: 70% of min(width, height) - 80px overhead, capped at 360
  const circleSize = Math.max(180, Math.min(360, Math.floor(Math.min(viewportSize.w, viewportSize.h - 280) - 40)));

  const content = (
    <div className={`fixed inset-0 z-[10001] bg-gradient-to-br ${phaseBg} flex flex-col overflow-hidden`} data-testid="table-execution-view">
      {/* Top bar */}
      <div className="flex items-center justify-between p-4 border-b border-blue-800/40 bg-blue-950/95 backdrop-blur-md shadow-lg">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleExitConfirm}
            className="text-blue-200 hover:bg-blue-800/50 min-h-[44px]"
            data-testid="exit-execution-btn"
          >
            <X size={20} />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest text-cyan-400 font-semibold flex items-center gap-1">
              <Activity size={11} className="opacity-70" />
              Table d'apnée en cours
            </div>
            <h2 className="text-base md:text-lg font-bold text-blue-100 truncate" data-testid="execution-table-name">{table.name}</h2>
            <div className="flex items-center gap-2 flex-wrap text-[11px] text-blue-400">
              <span className="px-1.5 py-0.5 bg-blue-800/40 rounded border border-blue-700/40">{table.type}</span>
              {table.difficulty && <span className="px-1.5 py-0.5 bg-blue-800/40 rounded border border-blue-700/40">{table.difficulty.replace('_', ' ').toLowerCase()}</span>}
              {!pbMode && <span>• Round {currentRound} / {totalRounds} <span className="opacity-70">— Phase {phaseInRound}/{phasesInCurrentRound}</span></span>}
              {pbMode && <span>• Mode Personal Best</span>}
              {table.description && <span className="hidden md:inline italic opacity-70">— {table.description}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAudioEnabled(!audioEnabled)}
            className="text-blue-200 min-h-[44px] min-w-[44px]"
            title={audioEnabled ? 'Couper le son' : 'Activer le son'}
            data-testid="toggle-audio-btn"
          >
            {audioEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setVibrateEnabled(!vibrateEnabled)}
            className={`min-h-[44px] min-w-[44px] ${vibrateEnabled ? 'text-cyan-300' : 'text-blue-500'}`}
            title="Vibration"
            data-testid="toggle-vibrate-btn"
          >
            <Vibrate size={20} />
          </Button>
        </div>
      </div>

      {/* Main timer area */}
      <div className="flex-1 flex flex-col items-center justify-center p-2 sm:p-4 pb-6 sm:pb-8 overflow-y-auto">
        <div className="text-center mb-2 sm:mb-4">
          <div
            className={`text-xl sm:text-2xl md:text-3xl font-bold uppercase tracking-widest ${
              isApnea ? 'text-red-400' : 'text-green-400'
            }`}
            data-testid="execution-phase-label"
          >
            {isApnea ? '🔴 Apnée' : '🟢 Ventilation'}
          </div>
          {!pbMode && stepDuration > 0 && (
            <div className="text-blue-300 text-xs sm:text-sm font-mono mt-1" data-testid="execution-phase-duration">
              {formatMMSS(stepDuration)}
            </div>
          )}
        </div>

        {/* Dual timer (analog circular + digital) */}
        <div className="relative mb-3 sm:mb-6">
          <CircularProgress
            progress={overallProgress}
            innerProgress={stepProgress}
            size={circleSize}
            stroke={Math.max(10, Math.floor(circleSize / 22))}
            color={phaseColor}
            innerColor={phaseColor}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-5xl sm:text-6xl md:text-8xl font-mono font-bold text-blue-100 tabular-nums" data-testid="execution-timer">
              {pbMode && isApnea ? formatMMSS(stepElapsed) : formatMMSS(stepRemaining)}
            </div>
            {!pbMode && (
              <div className="text-[10px] sm:text-xs md:text-sm text-blue-300 mt-1 sm:mt-2">
                Total : {formatMMSS(totalElapsed)} / {formatMMSS(totalDuration)}
              </div>
            )}
            {pbMode && isApnea && (
              <div className="text-xs sm:text-sm text-purple-300 mt-1 sm:mt-2 uppercase tracking-wider">
                Apnée libre
              </div>
            )}
          </div>
        </div>

        {/* Rounds progression ring — one dot per round, lights up as you progress */}
        {!pbMode && totalRounds > 1 && (
          <div
            className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 mb-2 sm:mb-3 max-w-md px-2"
            data-testid="rounds-progress-ring"
            aria-label={`Progression : round ${currentRound} sur ${totalRounds}`}
          >
            {Array.from({ length: totalRounds }).map((_, i) => {
              const roundNum = i + 1;
              const allDone = completed && !partialEnd;
              const isDone = allDone ? true : roundNum < currentRound;
              const isCurrent = !allDone && roundNum === currentRound;
              const sizeCls = totalRounds > 12 ? 'w-2 h-2' : totalRounds > 8 ? 'w-2.5 h-2.5' : 'w-3 h-3';
              return (
                <span
                  key={roundNum}
                  title={`Round ${roundNum}`}
                  data-testid={`round-dot-${roundNum}`}
                  data-state={isDone ? 'done' : isCurrent ? 'current' : 'upcoming'}
                  className={`rounded-full ${sizeCls} transition-all duration-300 ${
                    isDone
                      ? 'bg-cyan-400 shadow-[0_0_6px_rgba(34,211,238,0.55)]'
                      : isCurrent
                      ? 'bg-cyan-400 ring-2 ring-cyan-300/40 animate-pulse shadow-[0_0_12px_rgba(34,211,238,0.9)] scale-125'
                      : 'bg-transparent border border-blue-500/60'
                  }`}
                />
              );
            })}
          </div>
        )}

        {/* Next step / next round preview */}
        {!pbMode && currentStepIdx + 1 < steps.length && (
          <div className="flex flex-col items-center gap-1 mt-1" data-testid="execution-next-preview">
            {/* If we're on phase 1 of a round, show the upcoming phase 2 of the same round */}
            {phaseInRound === 1 && (
              <div className="text-blue-400 text-sm flex items-center gap-2">
                <ChevronRight size={14} />
                Suivant (round {currentRound}) :{' '}
                {steps[currentStepIdx + 1].phase === 'apnea' ? '🔴 Apnée' : '🟢 Ventilation'} —{' '}
                <span className="font-mono">{formatMMSS(steps[currentStepIdx + 1].duration_seconds)}</span>
              </div>
            )}
            {/* If a next round exists, show it as a compact pair */}
            {nextRoundStepA && (
              <div className="text-blue-300/80 text-xs flex items-center gap-2 mt-0.5">
                <ChevronRight size={12} />
                Prochain round ({currentRound + 1}) :
                <span className="font-mono">
                  {nextRoundStepA.phase === 'apnea' ? '🔴' : '🟢'} {formatMMSS(nextRoundStepA.duration_seconds)}
                </span>
                {nextRoundStepB && (
                  <>
                    <span className="opacity-60">→</span>
                    <span className="font-mono">
                      {nextRoundStepB.phase === 'apnea' ? '🔴' : '🟢'} {formatMMSS(nextRoundStepB.duration_seconds)}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Contractions panel — visible during apnea */}
        {isApnea && (
          <Card className="bg-red-950/40 border-red-700/50 p-4 mt-6 w-full max-w-md" data-testid="contractions-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="text-red-200 text-sm font-medium flex items-center gap-2">
                <Activity size={16} /> Contractions
              </span>
              <span className="text-red-300 font-bold text-2xl" data-testid="contractions-count">
                {contractionsThisStep.length}
              </span>
            </div>
            {firstContraction != null && (
              <div className="text-red-300 text-xs mb-2">
                1ère contraction : <span className="font-mono">{formatMMSS(firstContraction)}</span>
              </div>
            )}
            <Button
              onClick={handleAddContraction}
              disabled={!running}
              className="w-full bg-red-600 hover:bg-red-700 min-h-[56px] text-base"
              data-testid="add-contraction-btn"
            >
              + Contraction
            </Button>
          </Card>
        )}
      </div>

      {/* Bottom controls */}
      <div className="p-4 border-t border-blue-800/40 bg-blue-950/95 backdrop-blur-md shadow-[0_-4px_16px_rgba(0,0,0,0.3)]">
        {completed ? (
          <div className="text-center space-y-3">
            <div className="text-2xl font-bold text-green-300">
              {partialEnd ? '⏹ Table terminée (partielle)' : '✓ Terminé !'}
            </div>
            {partialEnd && (
              <div className="text-yellow-200 text-sm">
                Rounds complétés : {Math.floor(currentStepIdx / 2)} / {totalRounds}
                <span className="opacity-70 ml-2">(arrêté en phase {phaseInRound}/{phasesInCurrentRound} du round {currentRound})</span>
              </div>
            )}
            {pbMode && (
              <div className="text-cyan-300">
                Durée d'apnée : <span className="font-mono text-3xl font-bold">{formatMMSS(pbAchieved || stepElapsed)}</span>
              </div>
            )}
            {!pbMode && pbAchieved > 0 && (
              <div className="text-cyan-300 text-sm">
                Apnée la plus longue tenue : <span className="font-mono font-bold">{formatMMSS(pbAchieved)}</span>
              </div>
            )}
            {contractions.length > 0 && (
              <div className="text-blue-300 text-sm">
                Contractions totales : {contractions.length}
              </div>
            )}
            {savedSuccess && (
              <div className="bg-green-900/40 border border-green-700/50 text-green-200 text-sm p-2 rounded-lg" data-testid="saved-success">
                ✓ Session enregistrée
              </div>
            )}
            <div className="flex gap-3 justify-center flex-wrap">
              <Button
                onClick={() => setSaveDialog(true)}
                className="bg-cyan-600 hover:bg-cyan-700 min-h-[56px] px-6"
                data-testid="save-session-btn"
              >
                <Save className="mr-2" size={18} />
                Enregistrer
              </Button>
              <Button
                onClick={handleShareRecap}
                className="bg-purple-600 hover:bg-purple-700 min-h-[56px] px-6"
                data-testid="share-recap-btn"
              >
                <Share2 className="mr-2" size={18} />
                Partager le récap
              </Button>
              <Button onClick={handleReset} className="bg-blue-600 hover:bg-blue-700 min-h-[56px] px-6" data-testid="reset-after-completion-btn">
                <RotateCcw className="mr-2" size={18} />
                Recommencer
              </Button>
              <Button
                onClick={() => {
                  releaseWakeLock();
                  try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
                  onExit();
                }}
                variant="outline"
                className="border-blue-600 text-blue-200 min-h-[56px] px-6"
                data-testid="exit-after-completion-btn"
              >
                Fermer
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 max-w-md mx-auto">
            {!running && stepElapsed === 0 && totalElapsed === 0 ? (
              <Button onClick={handleStart} className="bg-green-600 hover:bg-green-700 min-h-[72px] text-2xl" data-testid="start-execution-btn">
                <Play className="mr-2" size={28} /> Commencer
              </Button>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={handlePauseResume}
                    className={`min-h-[72px] text-xl ${
                      running ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'
                    }`}
                    data-testid="pause-resume-btn"
                  >
                    {running ? (
                      <>
                        <Pause className="mr-2" size={24} /> Pause
                      </>
                    ) : (
                      <>
                        <Play className="mr-2" size={24} /> Reprendre
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleReset}
                    variant="outline"
                    className="border-blue-600 text-blue-200 min-h-[72px] text-xl"
                    data-testid="reset-execution-btn"
                  >
                    <RotateCcw className="mr-2" size={24} />
                    Reset
                  </Button>
                </div>
                {!pbMode && (
                  <Button
                    onClick={() => setConfirmEndTable(true)}
                    variant="outline"
                    className="border-red-700/50 text-red-300 hover:bg-red-900/30 min-h-[56px] text-base"
                    data-testid="end-table-btn"
                  >
                    <Square className="mr-2" size={18} />
                    Terminer la table (sauvegarder partiellement)
                  </Button>
                )}
              </>
            )}
            {pbMode && isApnea && running && (
              <Button
                onClick={handleEndApneaPB}
                className="bg-red-600 hover:bg-red-700 min-h-[88px] text-3xl font-bold"
                data-testid="end-pb-apnea-btn"
              >
                <Square className="mr-2" size={32} /> Fin Apnée
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Save session dialog */}
      {saveDialog && (
        <div className="fixed inset-0 bg-black/80 z-[110] flex items-center justify-center p-4" data-testid="save-session-dialog">
          <Card className="bg-blue-950 border-blue-700 p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-blue-100 mb-3 flex items-center gap-2">
              <Save size={20} /> Enregistrer la session
            </h3>
            <div className="space-y-3 text-sm">
              <div className="bg-blue-900/40 rounded-lg p-3 text-blue-200">
                <div className="flex justify-between">
                  <span>Table :</span>
                  <span className="font-medium">{table.name}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span>Durée :</span>
                  <span className="font-mono font-bold text-cyan-300">{formatMMSS(totalElapsed)}</span>
                </div>
                {pbAchieved > 0 && (
                  <div className="flex justify-between mt-1">
                    <span>Max apnée :</span>
                    <span className="font-mono font-bold text-cyan-300">{formatMMSS(pbAchieved)}</span>
                  </div>
                )}
                {contractions.length > 0 && (
                  <div className="flex justify-between mt-1">
                    <span>Contractions :</span>
                    <span className="font-bold">{contractions.length}</span>
                  </div>
                )}
              </div>

              {freedivers.length > 0 && (
                <div>
                  <label className="text-blue-200 block mb-1">Apnéiste (optionnel)</label>
                  <Select value={selectedFreediverId || 'none'} onValueChange={(v) => setSelectedFreediverId(v === 'none' ? '' : v)}>
                    <SelectTrigger className="bg-blue-950/60 border-blue-700/50 text-blue-100" data-testid="save-freediver-select">
                      <SelectValue placeholder="Aucun apnéiste" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun apnéiste</SelectItem>
                      {freedivers.map((fd) => (
                        <SelectItem key={fd.id} value={fd.id}>
                          {fd.first_name} {fd.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <label className="text-blue-200 block mb-1">Notes</label>
                <textarea
                  value={sessionNotes}
                  onChange={(e) => setSessionNotes(e.target.value)}
                  placeholder="Sensations, conditions..."
                  rows={2}
                  className="w-full bg-blue-950/60 border border-blue-700/50 rounded text-blue-100 p-2 text-sm"
                  data-testid="save-notes-input"
                />
              </div>

              {pbMode && selectedFreediverId && (
                <label className="flex items-start gap-2 cursor-pointer p-2 bg-purple-900/30 rounded border border-purple-700/40">
                  <input
                    type="checkbox"
                    checked={saveToCareer}
                    onChange={(e) => setSaveToCareer(e.target.checked)}
                    className="mt-1"
                    data-testid="save-to-career-checkbox"
                  />
                  <span className="text-purple-200 text-xs">
                    🏆 Enregistrer comme record personnel (Performance STA) dans le suivi de carrière
                  </span>
                </label>
              )}
            </div>
            <div className="flex gap-2 mt-4 justify-end">
              <Button
                variant="outline"
                onClick={() => setSaveDialog(false)}
                className="border-blue-600 text-blue-200"
                data-testid="cancel-save-btn"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSaveSession}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="confirm-save-btn"
              >
                <Save className="mr-2" size={16} />
                Enregistrer
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Confirm End Table dialog */}
      {confirmEndTable && (
        <div className="fixed inset-0 bg-black/80 z-[110] flex items-center justify-center p-4">
          <Card className="bg-blue-950 border-blue-700 p-6 max-w-md">
            <h3 className="text-xl font-bold text-blue-100 mb-2">Terminer la table ?</h3>
            <p className="text-blue-300 text-sm mb-4">
              Vous avez complété <strong>{currentStepIdx}</strong> étape(s) sur <strong>{steps.length}</strong>.
              La session sera enregistrée comme <strong>partielle</strong>. Les étapes restantes seront ignorées.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setConfirmEndTable(false)}
                className="border-blue-600 text-blue-200"
                data-testid="cancel-end-table-btn"
              >
                Continuer
              </Button>
              <Button
                onClick={handleEndTable}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-end-table-btn"
              >
                <Square className="mr-2" size={16} />
                Terminer
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Resume prompt (in-app dialog, replaces native confirm) */}
      {resumeData && (
        <div className="fixed inset-0 bg-black/80 z-[120] flex items-center justify-center p-4" data-testid="resume-dialog">
          <Card className="bg-blue-950 border-blue-700 p-6 max-w-md">
            <h3 className="text-xl font-bold text-blue-100 mb-2 flex items-center gap-2">
              <RotateCcw size={20} className="text-cyan-400" /> Reprendre l'exécution ?
            </h3>
            <p className="text-blue-300 text-sm mb-4">
              Une exécution était en cours pour cette table. Voulez-vous reprendre où vous en étiez, ou recommencer depuis le début ?
            </p>
            <div className="flex gap-3 justify-end flex-wrap">
              <Button
                variant="outline"
                onClick={handleResumeDecline}
                className="border-blue-600 text-blue-200"
                data-testid="resume-decline-btn"
              >
                Recommencer
              </Button>
              <Button
                onClick={handleResumeAccept}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="resume-accept-btn"
              >
                <Play className="mr-2" size={16} /> Reprendre
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Exit confirmation */}
      {confirmExit && (
        <div className="fixed inset-0 bg-black/80 z-[110] flex items-center justify-center p-4">
          <Card className="bg-blue-950 border-blue-700 p-6 max-w-md">
            <h3 className="text-xl font-bold text-blue-100 mb-2">Quitter l'exécution ?</h3>
            <p className="text-blue-300 text-sm mb-4">
              Votre progression sera enregistrée et vous pourrez reprendre plus tard.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setConfirmExit(false)}
                className="border-blue-600 text-blue-200"
                data-testid="cancel-exit-btn"
              >
                Continuer
              </Button>
              <Button
                onClick={() => {
                  releaseWakeLock();
                  setRunning(false);
                  onExit();
                }}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-exit-btn"
              >
                Quitter
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );

  // Render via React Portal to escape any parent stacking context
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(content, document.body);
};

export default TableExecutionView;
